/**
 * Sticker Label Printer Service
 *
 * Persisted slide/block labels are dispatched through the authenticated
 * backend endpoint. This keeps permission, item eligibility, idempotency, and
 * audit checks on the server even when a localhost bridge is available.
 * Accessioning labels do not have persisted rows yet and retain their separate
 * local-first dispatch path below.
 */

import { deviceBridgeService } from "./deviceBridge.service";
import { stickerService } from "./stickerService";
import {
  isUsablePatientLabelName,
  MISSING_PATIENT_LABEL_NAME_ERROR,
} from "../utils/patientLabelName";
import {
  formatDateOnlyWithShortMonth,
  requireDateOnlyString,
  type DateOnlyString,
} from "../utils/dateOnly";

export const MIN_DYMO_SLIDE_TEMPLATE_VERSION = "1.10";

export function isStickerTemplateVersionAtLeast(
  actualVersion: string | null | undefined,
  minimumVersion: string
): boolean {
  if (!actualVersion) return false;

  const actualParts = actualVersion.split(".").map((part) => Number(part));
  const minimumParts = minimumVersion.split(".").map((part) => Number(part));
  if (actualParts.some(Number.isNaN) || minimumParts.some(Number.isNaN)) {
    return false;
  }

  const length = Math.max(actualParts.length, minimumParts.length);
  const firstDifference = Array.from(
    { length },
    (_, index) => (actualParts[index] ?? 0) - (minimumParts[index] ?? 0)
  ).find((difference) => difference !== 0);

  return firstDifference === undefined || firstDifference > 0;
}

export const getStickerTemplateUpdateMessage = (
  minimumVersion: string,
  actualVersion: string | null | undefined
) =>
  `Update and re-register the Dymo bridge before printing these labels. Expected template ${minimumVersion} or newer, got ${
    actualVersion || "unknown"
  }.`;

export interface StickerLabelData {
  itemSlotId: string;
  itemType: "slide" | "block" | "requisition";
  caseId: string;
  itemId: string;
  caseNumber: string;
  patientName: string;
  specimen: string;
  blockId: string;
  slideId: string;
  stain: string;
  dateReceived?: DateOnlyString;
  dateOfService?: string;
  partsLabel?: string;
  barcodeValue: string;
  // Marks the dispatch as a reprint in the audit row. No reason required —
  // mirrors slide reprint UX (one-click).
  isReprint?: boolean;
}

export interface StickerPrintResult {
  success: boolean;
  method: "local" | "backend";
  count: number;
  error?: string;
}

function formatDateReceivedForDymo(value: DateOnlyString | undefined): string {
  if (value === undefined) return "";
  const dateReceived = requireDateOnlyString(value, "Date received");
  return formatDateOnlyWithShortMonth(dateReceived, { twoDigitYear: true });
}

function requireStickerLabelDates(labels: StickerLabelData[]): void {
  labels.forEach((label) => {
    if (label.dateReceived !== undefined) {
      requireDateOnlyString(label.dateReceived, "Date received");
    }
  });
}

export function buildBridgeItems(labels: StickerLabelData[]) {
  return labels.map((l) => ({
    itemSlotId: l.itemSlotId,
    itemType: l.itemType,
    itemId: l.itemId,
    caseId: l.caseId,
    barcodeValue: l.barcodeValue,
    fields: {
      caseNumber: l.caseNumber,
      specimen: l.specimen,
      blockId: l.blockId,
      slideId: l.slideId,
      patientName: l.patientName,
      stain: l.stain,
      dateReceived: formatDateReceivedForDymo(l.dateReceived),
      dateOfService: l.dateOfService,
      partsLabel: l.partsLabel,
    },
    clientRequestId: l.itemSlotId,
    isReprint: !!l.isReprint,
  }));
}

export const stickerLabelPrinterService = {
  /**
   * Dispatch persisted sticker labels through the authenticated backend, one
   * request per item, so every item is independently validated and audited.
   */
  async printStickerLabels(
    labels: StickerLabelData[],
    _deviceId: string,
    workstationId: string,
    stickerTemplateVersion?: string | null
  ): Promise<StickerPrintResult> {
    if (labels.length === 0) {
      return {
        success: false,
        method: "backend",
        count: 0,
        error: "No labels to print",
      };
    }

    requireStickerLabelDates(labels);

    if (
      labels.some((label) => label.itemType === "slide") &&
      !isStickerTemplateVersionAtLeast(
        stickerTemplateVersion,
        MIN_DYMO_SLIDE_TEMPLATE_VERSION
      )
    ) {
      return {
        success: false,
        method: "backend",
        count: 0,
        error: getStickerTemplateUpdateMessage(
          MIN_DYMO_SLIDE_TEMPLATE_VERSION,
          stickerTemplateVersion
        ),
      };
    }

    if (
      labels.some(
        (label) =>
          label.itemType !== "requisition" &&
          !isUsablePatientLabelName(label.patientName)
      )
    ) {
      return {
        success: false,
        method: "backend",
        count: 0,
        error: MISSING_PATIENT_LABEL_NAME_ERROR,
      };
    }

    if (labels.some((label) => label.itemType === "requisition")) {
      return {
        success: false,
        method: "backend",
        count: 0,
        error: "Requisition labels must use accessioning sticker dispatch.",
      };
    }

    const persistedLabels = labels as Array<
      StickerLabelData & { itemType: "slide" | "block" }
    >;

    const responses = await Promise.allSettled(
      persistedLabels.map((l) =>
        l.isReprint
          ? stickerService.reprintSticker({
              itemId: l.itemId,
              itemType: l.itemType,
              caseId: l.caseId,
              workstationId,
              clientRequestId: l.itemSlotId,
              isReprint: true,
            })
          : stickerService.printSticker({
              itemId: l.itemId,
              itemType: l.itemType,
              caseId: l.caseId,
              workstationId,
              clientRequestId: l.itemSlotId,
            })
      )
    );
    const ok = responses.filter((r) => r.status === "fulfilled").length;
    const failed = responses.length - ok;
    if (failed === 0) {
      return { success: true, method: "backend", count: ok };
    }
    return {
      success: ok > 0,
      method: "backend",
      count: ok,
      error: `${failed} of ${labels.length} sticker print(s) failed`,
    };
  },

  /**
   * Dispatch sticker labels for slides that are NOT yet persisted (printed
   * during accessioning). The backend HTTP /sticker/print endpoint validates
   * against a persisted slide or block row, which does not exist yet — so we
   * cannot use it. Instead:
   *   1. If the frontend is connected directly to a local bridge, send over
   *      that socket (fast path, no backend hop).
   *   2. Otherwise dispatch via the backend, which relays the command to the
   *      workstation bridge over its own WebSocket. This is the common case:
   *      the bridge registers with the backend (so the device shows
   *      "connected"), while the frontend talks to the backend relay rather
   *      than a local socket.
   */
  async printAccessioningStickerLabels(
    labels: StickerLabelData[],
    deviceId: string,
    workstationId: string,
    stickerTemplateVersion?: string | null
  ): Promise<StickerPrintResult> {
    if (labels.length === 0) {
      return {
        success: false,
        method: "local",
        count: 0,
        error: "No labels to print",
      };
    }

    requireStickerLabelDates(labels);

    if (
      labels.some((label) => label.itemType === "slide") &&
      !isStickerTemplateVersionAtLeast(
        stickerTemplateVersion,
        MIN_DYMO_SLIDE_TEMPLATE_VERSION
      )
    ) {
      return {
        success: false,
        method: "local",
        count: 0,
        error: getStickerTemplateUpdateMessage(
          MIN_DYMO_SLIDE_TEMPLATE_VERSION,
          stickerTemplateVersion
        ),
      };
    }

    if (
      labels.some(
        (label) =>
          label.itemType !== "requisition" &&
          !isUsablePatientLabelName(label.patientName)
      )
    ) {
      return {
        success: false,
        method: "local",
        count: 0,
        error: MISSING_PATIENT_LABEL_NAME_ERROR,
      };
    }

    const items = buildBridgeItems(labels);

    // Fast path: direct local bridge connection.
    if (deviceBridgeService.isLocal()) {
      try {
        const result = await deviceBridgeService.sendCommand(
          { type: "print_sticker", payload: { deviceId, items } },
          30000
        );
        const payload = result.payload ?? {};
        if (payload.success !== false) {
          return { success: true, method: "local", count: labels.length };
        }
        return {
          success: false,
          method: "local",
          count: 0,
          error: (payload.error as string) || "Print failed via local bridge",
        };
      } catch {
        // Local socket failed — fall through to the backend relay.
      }
    }

    // Backend relay: the server owns the bridge connection.
    try {
      const res = await stickerService.dispatchStickerLabels({
        workstationId,
        deviceId,
        items,
      });
      if (res?.delivered) {
        return { success: true, method: "backend", count: labels.length };
      }
      return {
        success: false,
        method: "backend",
        count: 0,
        error:
          "The label printer is not currently online at this workstation. Confirm the bridge app is running and the DYMO printer is connected, then try again.",
      };
    } catch (e: any) {
      return {
        success: false,
        method: "backend",
        count: 0,
        error:
          e?.response?.data?.message || "Could not dispatch sticker labels.",
      };
    }
  },
};
